import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { MenuItem, TextField } from "@mui/material";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from "chart.js";
import { Download } from "react-feather";
import { withSuperAdminAuth } from "../../../components/GetAuth";
import PageHeader from "../../../components/PageHeader";
import { downloadFile, formatGBP, formatGBP2 } from "../../../components/Features";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import { Kpi, saGet, TableSkeleton, Toast, useToast } from "../shared";
import styles from "../superadmin.module.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const GROUP_LABEL = { month: "Month", addon: "Plan / add-on", company: "Company" };

const RevenueReport = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, showMessage, closeToast] = useToast();

  const from = searchParams.get("from") || dayjs().startOf("month").subtract(11, "month").format("YYYY-MM-DD");
  const to = searchParams.get("to") || dayjs().endOf("month").format("YYYY-MM-DD");
  const group = searchParams.get("group") || "month";

  const { websiteData } = useWebsite();
  useSiteBranding("Revenue", websiteData);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    setLoading(true);
    saGet("revenue", { from, to, group }).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") setData(response.data.data);
    }).catch(() => setLoading(false));
  }, [from, to, group]);

  const exportCsv = () => {
    downloadFile("v1/admin/revenue/export", `revenue-${group}-${from}-${to}.csv`, { from, to, group }).catch(() => showMessage("Export failed.", "error"));
  };

  const rows = data?.rows || [];
  const chartRows = group === "company" ? rows.slice(0, 15) : rows;
  const chart = {
    labels: chartRows.map((r) => r.label),
    datasets: [
      { label: "Collected", data: chartRows.map((r) => r.collected), backgroundColor: "#ef233c", borderRadius: 4, maxBarThickness: 26, stack: "a" },
      { label: "Outstanding", data: chartRows.map((r) => r.outstanding), backgroundColor: "#f5a3ae", borderRadius: 4, maxBarThickness: 26, stack: "a" },
    ],
  };
  const collectionRate = data?.totals?.invoiced > 0 ? Math.round((data.totals.collected / data.totals.invoiced) * 100) : 0;

  return (<div className={`${styles.Page}`}>
    <PageHeader title="Revenue" trail={[{ label: "Billing", to: "/super-admin/invoices" }]} />

    <div className={`${styles.Toolbar}`}>
      <TextField size="small" type="date" label="From" value={from} onChange={(e) => setParam("from", e.target.value)} InputLabelProps={{ shrink: true }} className={`${styles.Filter}`} />
      <TextField size="small" type="date" label="To" value={to} onChange={(e) => setParam("to", e.target.value)} InputLabelProps={{ shrink: true }} className={`${styles.Filter}`} />
      <TextField size="small" select label="Group by" value={group} onChange={(e) => setParam("group", e.target.value)} className={`${styles.Filter}`}>
        <MenuItem value="month">Month</MenuItem>
        <MenuItem value="addon">Plan / add-on</MenuItem>
        <MenuItem value="company">Company</MenuItem>
      </TextField>
      <div className={`${styles.ToolbarRight}`}>
        <button type="button" className={`${styles.ActionBU}`} onClick={exportCsv}><Download />Export CSV</button>
      </div>
    </div>

    {loading && <TableSkeleton kpis={4} rows={6} />}

    {!loading && data && (<>
      <div className={`${styles.KpiGrid}`}>
        <Kpi label="Invoiced" value={formatGBP(data.totals.invoiced)} sub={`${dayjs(data.from).format("DD MMM YY")} – ${dayjs(data.to).format("DD MMM YY")}`} />
        <Kpi label="Collected" value={formatGBP(data.totals.collected)} sub={`${collectionRate}% collection rate`} />
        <Kpi label="Outstanding" value={formatGBP(data.totals.outstanding)} danger={data.totals.outstanding > 0} />
        <Kpi label="Current MRR" value={formatGBP(data.mrr)} sub="Active companies, Basic + add-ons" />
      </div>

      <div className={`${styles.Panel}`} style={{ marginBottom: 18 }}>
        <p className={`${styles.PanelTitle}`} style={{ marginBottom: 14 }}>By {GROUP_LABEL[group].toLowerCase()}{group === "company" && rows.length > 15 ? " (top 15)" : ""}</p>
        <div className={`${styles.ChartBox}`}>
          <Bar data={chart} options={{ indexAxis: group === "month" ? "x" : "y", responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "bottom" }, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${formatGBP2(c.raw)}` } } }, scales: { x: { stacked: true, grid: { display: group !== "month" } }, y: { stacked: true, grid: { display: group === "month" } } } }} />
        </div>
      </div>

      <div className={`${styles.TableWrap}`}>
        <table className={`${styles.Table}`}>
          <thead>
            <tr><th>{GROUP_LABEL[group]}</th>{group !== "addon" && <th className={`${styles.Right}`}>Invoices</th>}<th className={`${styles.Right}`}>Invoiced</th><th className={`${styles.Right}`}>Collected</th><th className={`${styles.Right}`}>Outstanding</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (<tr key={r.key}>
              <td>{r.label}</td>
              {group !== "addon" && <td className={`${styles.Right}`}>{r.invoices}</td>}
              <td className={`${styles.Right}`}>{formatGBP2(r.invoiced)}</td>
              <td className={`${styles.Right}`}>{formatGBP2(r.collected)}</td>
              <td className={`${styles.Right} ${r.outstanding > 0 ? styles.Danger : ""}`}>{formatGBP2(r.outstanding)}</td>
            </tr>))}
            {rows.length === 0 && <tr><td colSpan={5} style={{ textAlign: "center", padding: 30 }}>No invoices in this period.</td></tr>}
          </tbody>
          {rows.length > 0 && <tfoot>
            <tr><td>Total</td>{group !== "addon" && <td className={`${styles.Right}`}>{rows.reduce((s, r) => s + (r.invoices || 0), 0)}</td>}<td className={`${styles.Right}`}>{formatGBP2(data.totals.invoiced)}</td><td className={`${styles.Right}`}>{formatGBP2(data.totals.collected)}</td><td className={`${styles.Right}`}>{formatGBP2(data.totals.outstanding)}</td></tr>
          </tfoot>}
        </table>
      </div>
    </>)}

    <Toast toast={toast} onClose={closeToast} />
  </div>);
}

export default withSuperAdminAuth(RevenueReport);
